import { effect, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { formatISO } from 'date-fns';
import { firstValueFrom, Subject } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { StorageKeysEnum } from '../enums/storage.keys.enum';
import { WebSocketGenericMessage, WebSocketMessage, WebSocketTypeEnum } from './websocket.types';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class WebsocketService {

    private _newGenericMessage = signal<WebSocketGenericMessage>(undefined);
    readonly newGenericMessage = this._newGenericMessage.asReadonly();
    private _genericMessages = signal<WebSocketGenericMessage[]>([]);
    readonly genericMessages = this._genericMessages.asReadonly();

    private serverInfo = toSignal(this.authService.serverInfo);
    private webSocket: WebSocket;
    private opened: boolean = false;
    private closed: boolean = true;
    private websocketServerURL: string;
    private accessToken: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private authService: AuthService,
    ) {
        const stringMessages = localStorage.getItem(StorageKeysEnum.genericMessages);
        if (stringMessages) {
            this._genericMessages.set(JSON.parse(stringMessages));
        } else {
            this._genericMessages.set([]);
        }
        this.authService.deleteGenericMessages
            .subscribe(() => this.setGenericMessages([]));

        effect(() => {
            if (this.serverInfo()) {
                //this.connect();
            }
        });
    }

    setGenericMessages(genericMessages: WebSocketGenericMessage[]) {
        this._genericMessages.set(genericMessages);
        localStorage.setItem(StorageKeysEnum.genericMessages, JSON.stringify(genericMessages));
    }

    async connect() {
        // open the connection if one does not exist
        if (this.webSocket !== undefined && this.webSocket.readyState !== WebSocket.CLOSED) {
            return;
        }
        this.websocketServerURL = this.serverInfo().websocketServerURL;
        if (!this.authService.accessTokenIsValid()) {
            if (this.authService.refreshTokenIsValid()) {
                try {
                await firstValueFrom(this.authService.doRefreshToken());
                } catch (error) {
                    console.error(error);
                    return;
                }
            } else {
                return;
            }
        }
        this.accessToken = this.authService.accessToken;
        const wsUrl = this.websocketServerURL + '/' + encodeURI(environment.applicationId) + '/' + encodeURI(this.accessToken);

        const textDecode = new TextDecoder('utf-8');

        // Create a websocket
        this.webSocket = new WebSocket(wsUrl);
        this.webSocket.binaryType = 'arraybuffer';

        this.webSocket.onopen = (event) => {
            this.opened = true;
            this.closed = false;
        };

        this.webSocket.onmessage = (event) => {
            if (event.data instanceof ArrayBuffer) {
                const msg: WebSocketMessage = JSON.parse(textDecode.decode(event.data));
                switch (msg.type) {
                    case WebSocketTypeEnum.GENERIC_ALERT:
                        const messages = this._genericMessages();
                        const newMessage: WebSocketGenericMessage = { ...msg, receivedDate: formatISO(new Date()) };
                        messages.unshift(newMessage);
                        this._genericMessages.set([...messages]);
                        this._newGenericMessage.set(newMessage);
                        break;
                }
            }
        };

        this.webSocket.onclose = (event) => {
            this.opened = false;
            this.closed = true;
            this.connect();
        };
    }
}
